import { Box, Button, Fade, Modal, Typography, useTheme } from '@mui/material';
import type { FC } from 'react';

import { CONFIRM_DIALOG_MAX_WIDTH } from '@web/constants/app';

import type { ConfirmDialogData } from './ConfirmContext';

interface ConfirmDialogProps {
  isOpen: boolean;
  alert: ConfirmDialogData | null;
  onClose(): void;
  onClearDialogData(): void;
}

const DURATION = 300;

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  isOpen,
  alert,
  onClose,
  onClearDialogData,
}) => {
  const theme = useTheme();

  return (
    <Modal
      closeAfterTransition
      open={isOpen}
      onClose={onClose}
      onTransitionExited={onClearDialogData}
      slotProps={{ backdrop: { timeout: DURATION } }}
    >
      <Fade in={isOpen} timeout={DURATION}>
        <Box
          sx={{
            outline: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 2,
          }}
          onClick={onClose}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              width: 1,
              maxWidth: alert?.maxWidth ?? CONFIRM_DIALOG_MAX_WIDTH,
              padding: 3,
              borderRadius: 2.5,
              backgroundColor: theme.palette.common.white,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Typography variant='h4'>{alert?.title}</Typography>
              <Typography variant='body3'>{alert?.description}</Typography>
            </Box>

            {alert !== null && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {alert.actions.map(({ label, onClick, ...props }) => (
                  <Button
                    sx={{ flex: 1, whiteSpace: 'nowrap' }}
                    key={label}
                    onClick={(d) => {
                      onClose();
                      onClick?.(d);
                    }}
                    {...props}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
